import { useState, useEffect } from 'react';
import { UploadedMethodType, useOnboardingStore } from '../../../store/onboardingStore';
import ProductInformationManualEntry from '../components/ProductInformationManualEntry';
import ImportProductFromLinkHeader from '../components/ImportProductFromLinkHeader';
import ImportProductFromLink from '../components/ImportProductFromLink';
import RenderEntryMethodSelection from '../components/RenderEntryMethodSelection';
import SearchPreviousProducts from '../components/SearchPreviousProducts';
import ButtonResetTypeProduct from '../components/ButtonResetTypeProdct';
import { useAuth } from '../../../context/AuthContext';
import { dictionary } from '../../../utils/dictionary';
import { TypeProduct, MAX_IMAGES, MAX_FILE_SIZE, FOLDER_IMAGES } from '../const/products';
import { useImageUpload } from '../../../hooks/useImageUpload';

export function StepProductData() {
  const { language } = useAuth();
  const {
    data,
    updateProductData,
    updateVisualContent,
    previousProducts,
    isPreviousProductsLoading,
    fetchPreviousProducts
  } = useOnboardingStore();
  const { productData } = data;
  const [productLink, setProductLink] = useState<string>(productData.productLink || '');
  const [typeProduct, setTypeProduct] = useState<TypeProduct>(TypeProduct.NONE);
  
  const {
    isLoading,
    error,
    handleImageUpload,
    removeImage,
    selectMainImage,
    selectedImageIndex,
    uploadProgress
  } = useImageUpload({
    maxImages: MAX_IMAGES,
    maxFileSize: MAX_FILE_SIZE,
    folder: FOLDER_IMAGES
  });
  
  useEffect(() => {
    if (!productData.warranty) {
      updateProductData({
        ...productData,
        warranty: '30days',
        customWarrantyDays: '30',
        freeShipping: productData.freeShipping ?? true
      });
    }
  }, []);

  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  const handleManualEntry = () => {
    setTypeProduct(TypeProduct.MANUAL);
    updateProductData({
      ...productData,
      name: '',
      price: '0.00',
      details: '',
      isManualEntry: true,
      productLink: '',
      providerId: null,
      externalId: null,
      uploadedMethod: UploadedMethodType.MANUAL
    });

    updateVisualContent({
      images: [],
    });
  };

  const handleLinkEntry = () => {
    setTypeProduct(TypeProduct.LINK);
    setProductLink('');
    updateProductData({
      ...productData,
      name: '',
      price: '',
      details: '',
      isManualEntry: false,
      productLink: '',
      uploadedMethod: UploadedMethodType.LINK
    });

    updateVisualContent({
      images: [],
    });
  };


  const handleProductLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProductLink(value);

    updateProductData({
      ...productData,
      productLink: value
    });
  };
  
  const handlePreviousProductSelectType = () => {
    setTypeProduct(TypeProduct.PREVIOUS);
    
    if (previousProducts.length === 0 && !isPreviousProductsLoading) {
      fetchPreviousProducts();
    }
  };

  const handlePreviousProductSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const productId = e.target.value;
    if (!productId) return;
    const selectedProduct = previousProducts.find(product => product.id === productId);
    if (!selectedProduct) return;

    updateProductData({
      name: selectedProduct.name,
      price: selectedProduct.price.toString(),
      details: selectedProduct.description,
      isManualEntry: false,
      externalId: selectedProduct.id,
      providerId: 'INTERNAL_FLUXI',
      uploadedMethod: UploadedMethodType.PREVIOUS
    });

    if (selectedProduct.images && selectedProduct.images.length > 0) {
      updateVisualContent({
        images: selectedProduct.images
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h3 className="text-xl font-medium mb-6">{dictionary["select-a-product-for-your-sales-funnel"][language]}</h3>
        {typeProduct !== TypeProduct.NONE && (
          <ButtonResetTypeProduct
            onClick={() => setTypeProduct(TypeProduct.NONE)}
          />
        )}
      </div>

      {typeProduct === TypeProduct.NONE && (
        <RenderEntryMethodSelection
          typeProduct={typeProduct} 
          handleLinkEntry={handleLinkEntry} 
          handleManualEntry={handleManualEntry} 
          handlePreviousProductSelectType={handlePreviousProductSelectType} 
        />
      )}

      {typeProduct === TypeProduct.LINK && (
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          {!isLoading && (
            <ImportProductFromLinkHeader />
          )}
          <ImportProductFromLink
            isLoading={isLoading}
            productLink={productLink}
            handleProductLinkChange={handleProductLinkChange}
            error={error}
          />
        </div>
      )}

      {typeProduct === TypeProduct.MANUAL && (
        <ProductInformationManualEntry
          data={data}
          handleImageUpload={handleImageUpload}
          selectMainImage={selectMainImage}
          removeImage={removeImage}
          selectedImageIndex={selectedImageIndex}
          productData={productData}
          updateProductData={updateProductData}
          MAX_IMAGES={MAX_IMAGES}
          uploadProgress={uploadProgress}
        />
      )}

      {typeProduct === TypeProduct.PREVIOUS && (
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          <SearchPreviousProducts
            previousProducts={previousProducts}
            handlePreviousProductSelect={handlePreviousProductSelect}
            isPreviousProductsLoading={isPreviousProductsLoading}
          />
        </div>
      )}

    </div>
  );
}